import {
    Component,
    Inject,
    LOCALE_ID,
    OnDestroy,
    OnInit,
    Renderer2,
    HostListener,
} from '@angular/core';
import { ConfigService } from '../@vex/services/config.service';
import { Settings } from 'luxon';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { NavigationService } from '../@vex/services/navigation.service';
import { Router } from '@angular/router';
import { Style } from '../@vex/services/style.service';
import { ConfigName } from '../@vex/interfaces/config-name.model';
import { colorVariables } from '../@vex/components/config-panel/color-variables';
import { appNavigationConfig } from './app-navigation';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { UserStore } from '@stores/user.store';
import { SettingsStore } from '@stores/settings.store';
import { AuthService } from '@app/auth/auth.service';
import { MsalAngularService } from '@services/msal-angular.service';
import { User } from '@entities/user.entity';
import { ApiService } from '@services/api.service';

@Component({
    selector: 'app-root',
    template: `
        <ng-container *ngIf="!isAuthChecking; else loadingScreen">
            <ng-container *ngIf="isAuthenticated">
                <app-loader></app-loader>
                <router-outlet *ngIf="!isIframe"></router-outlet>
            </ng-container>
        </ng-container>

        <ng-template #loadingScreen>
            <div class="loading-screen">
                <mat-spinner></mat-spinner>
                <p>Authenticating...</p>
            </div>
        </ng-template>
    `,
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    isAuthenticated = false;
    isAuthChecking = true;

    footerVisible = window.innerWidth <= 800;
    selectedTheme = ConfigName.ares;
    selectedStyle = Style.light;
    selectedColor = colorVariables['deep-orange'];
    isIframe = false;
    showFullMenu = false;
    private readonly _destroying$ = new Subject<void>();

    constructor(
        private configService: ConfigService,
        private renderer: Renderer2,
        private platform: Platform,
        @Inject(DOCUMENT) private document: Document,
        @Inject(LOCALE_ID) private localeId: string,
        private router: Router,
        private navigationService: NavigationService,
        private translate: TranslateService,
        private userStore: UserStore,
        private settingsStore: SettingsStore,
        private msalAngularService: MsalAngularService,
        private apiService: ApiService,
        private authService: AuthService,
    ) {
        // Settings.defaultLocale = this.localeId;
        Settings.defaultLocale = 'de';

        // Add languages
        this.translate.addLangs(['en', 'de']);

        // Set the default language
        this.translate.setDefaultLang(Settings.defaultLocale);
        this.translate.use(this.userStore.getUser()?._language || Settings.defaultLocale);

        if (this.platform.BLINK) {
            this.renderer.addClass(this.document.body, 'is-blink');
        }
        /*
		this.configService.setConfig(this.selectedTheme);
		this.styleService.setStyle(this.selectedStyle);
		if (this.document) {
			this.document.documentElement.style.setProperty('--color-primary', this.selectedColor.default.replace('rgb(', '').replace(')', ''));
			this.document.documentElement.style.setProperty('--color-primary-contrast', this.selectedColor.contrast.replace('rgb(', '').replace(')', ''));
		}
		*/
        this.configService.updateConfig({
            sidenav: {
                title: 'ITO Hub',
                imageUrl: 'assets/img/logo.png',
                showCollapsePin: false,
            },
            footer: {
                visible: window.innerWidth <= 800,
                fixed: true,
            },
        });

        if (this.footerVisible && !this.userStore.isLoggedIn()) {
            this.setFooterVisibility(false);
        }

        /**
         * Config Related Subscriptions
         * You can remove this if you don't need the functionality of being able to enable specific configs with queryParams
         * Example: example.com/?layout=apollo&style=default
         */
        /*
    this.route.queryParamMap.pipe(
      filter(queryParamMap => queryParamMap.has('rtl')),
      map(queryParamMap => coerceBooleanProperty(queryParamMap.get('rtl'))),
    ).subscribe(isRtl => {
      this.configService.updateConfig({
        rtl: isRtl
      });
    });
    this.route.queryParamMap.pipe(
      filter(queryParamMap => queryParamMap.has('layout'))
    ).subscribe(queryParamMap => this.configService.setConfig(queryParamMap.get('layout') as ConfigName));

    this.route.queryParamMap.pipe(
      filter(queryParamMap => queryParamMap.has('style'))
    ).subscribe(queryParamMap => this.styleService.setStyle(queryParamMap.get('style') as Style));
     */

        const moduleFilter = module => {
            return !module.hide;
        };
        this.navigationService.items = appNavigationConfig.filter(moduleFilter);
        this.settingsStore.settingsChanged$.subscribe(settings => {
            if (
                settings.hasOwnProperty('SHOW_FULL_MENU') &&
                settings['SHOW_FULL_MENU'] !== this.showFullMenu
            ) {
                this.showFullMenu = settings['SHOW_FULL_MENU'];
                if (this.showFullMenu) {
                    this.navigationService.items = appNavigationConfig;
                } else {
                    this.navigationService.items = appNavigationConfig.filter(moduleFilter);
                }
            }
        });
    }

    async ngOnInit() {
        this.isAuthenticated = await this.msalAngularService.fnInit();

        if (!this.isAuthenticated) {
            this.isAuthChecking = false;
            this.navigationService.items = [];
            this.setFooterVisibility(false);
            this.router.navigate(['/login-not-possible']);
            return;
        }

        const userFromDb = await this.apiService.get('users/me');

        this.authService.navigateToStoredRoute();

        this.isAuthChecking = false;

        this.userStore.updateUser(userFromDb);

        window.setTimeout(() => {
            const moduleFilter = module => {
                return !module.hide;
            };
            this.showFullMenu = this.settingsStore.get('SHOW_FULL_MENU');
            if (this.showFullMenu) {
                this.navigationService.items = appNavigationConfig;
            } else {
                this.navigationService.items = appNavigationConfig.filter(moduleFilter);
            }
        }, 333);

        if (window.innerWidth <= 800) {
            this.setFooterVisibility(true);
        }
    }

    private setFooterVisibility(val: boolean) {
        if (val != this.footerVisible) {
            this.footerVisible = val;
            this.configService.updateConfig({
                footer: {
                    visible: val,
                },
            });
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        if (window.innerWidth > 800) {
            this.setFooterVisibility(false);
        } else {
            if (this.userStore.isLoggedIn()) {
                this.setFooterVisibility(true);
            }
        }
    }

    // unsubscribe to events when component is destroyed
    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
}
